

// useCatalogOrder.js
import { useState, useEffect } from 'react';
import axios from 'axios';
import { CATALOG_URLS } from '../config/urls';

const useCatalogOrder = () => {
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Create a new catalog order
  const createOrder = async (orderType = "catalog") => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        CATALOG_URLS.createOrder,
        { order_type: orderType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newOrderId = response.data.id;
      console.log(newOrderId)
      setCurrentOrderId(newOrderId);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Error creating order');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get all catalog orders with optional filtering
  const getOrders = async (status = null, skip = 0, limit = 100) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = { skip, limit };
      if (status) params.status = status;
      
      const response = await axios.get(
        CATALOG_URLS.getAllOrders,
        {
          params,
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      return response.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Error fetching orders');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get details for a specific order
  const getOrderDetails = async (orderId) => {
    if (!orderId) return null;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        CATALOG_URLS.getOrder(orderId),
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setOrderDetails(response.data);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Error fetching order details');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Save or update a draft order
  const saveDraftOrder = async (orderData, orderIdParam) => {
    const orderIdToUse = orderIdParam || currentOrderId;
    if (!orderIdToUse) {
      throw new Error('No order ID provided');
    }
  
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Convert frontend status format to backend enum format if needed
      if (orderData.status === 'Requested') {
        orderData.status = 'requested';
      } else if (orderData.status === 'Draft') {
        orderData.status = 'draft';
      }
      
      const response = await axios.put(
        CATALOG_URLS.updateOrder(orderIdToUse),
        orderData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Update the local state with the updated order details
      setOrderDetails(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Error saving order';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Update order status (e.g., from draft to requested)
  const updateOrderStatus = async (orderId, status, message = null, estimatedDeliveryDate = null) => {
    if (!orderId) {
      throw new Error('No order ID provided');
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const statusData = {
        status,
        message,
        estimated_delivery_date: estimatedDeliveryDate
      };
      
      const response = await axios.put(
        CATALOG_URLS.updateOrderStatus(orderId),
        statusData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Error updating order status';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Delete a draft order
  const deleteOrder = async (orderId) => {
    if (!orderId) {
      throw new Error('No order ID provided');
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.delete(
        CATALOG_URLS.deleteOrder(orderId),
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // If we deleted the current order, clear it
      if (orderId === currentOrderId) {
        setCurrentOrderId(null);
        setOrderDetails(null);
      }
      
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Error deleting order';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Effect to fetch order details when currentOrderId changes
  useEffect(() => {
    if (currentOrderId) {
      getOrderDetails(currentOrderId).catch(err => {
        console.error('Failed to fetch order details:', err);
      });
    }
  }, [currentOrderId]);

  return {
    currentOrderId,
    setCurrentOrderId,
    orderDetails,
    loading,
    error,
    createOrder,
    getOrders,
    getOrderDetails,
    saveDraftOrder,
    updateOrderStatus,
    deleteOrder
  };
};

export default useCatalogOrder;